import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, FileInput, GeneralDialog, InputWithLabel } from "@ptsd/uikit";
import i18next from "i18next";
import { RECORD_SEP, UNIT_SEP } from "papaparse";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  Form,
  ScrollView,
  Separator,
  Text,
  View,
  XStack,
} from "tamagui";
import { z } from "zod";

import { api } from "~/utils/api";
import { chunkArray } from "~/utils/chunk-array";
import { parseCsv } from "~/utils/csv-parser";
import { useSendChunks } from "~/hooks/use-send-chunks";
import { InviteStatusTable } from "./invite-status-table";

const FILE_SIZE_LIMIT = 1024 * 1024;
const FILE_MINE_TYPES = [
  "text/csv",
  "application/vnd.ms-excel",
  "application/csv",
];

const emailArraySchema = z
  .array(
    z
      .string()
      .email({
        message: i18next.t(
          "group:invite-user.validation-errors.incorrect-email-in-csv",
        ),
      })
      .max(50, {
        message: i18next.t(
          "group:invite-user.validation-errors.email-max-length",
        ),
      }),
  )
  .min(1, {
    message: i18next.t(
      "group:invite-user.validation-errors.at-least-one-email",
    ),
  });

const formSchema = z
  .object({
    email: z
      .string()
      .email({
        message: i18next.t(
          "group:invite-user.validation-errors.incorrect-email",
        ),
      })
      .min(2, {
        message: i18next.t(
          "group:invite-user.validation-errors.email-min-length",
        ),
      })
      .max(50, {
        message: i18next.t(
          "group:invite-user.validation-errors.email-max-length-form",
        ),
      })
      .optional()
      .transform((value) => (value === "" ? undefined : value)),
    csv: z
      .instanceof(File)
      .refine((file) => FILE_MINE_TYPES.includes(file.type), {
        message: i18next.t(
          "group:invite-user.validation-errors.csv-invalid-type",
        ),
      })
      .refine((file) => file.size <= FILE_SIZE_LIMIT, {
        message: i18next.t("group:invite-user.validation-errors.csv-max-size"),
      })
      .optional(),
  })
  .refine((data) => {
    if (!data.email && !data.csv) {
      return false;
    }
    return true;
  });

type InviteToGroupForm = z.infer<typeof formSchema>;

export const InviteToGroupDialog: React.FC<{
  groupId: string;
}> = ({ groupId }) => {
  const [open, setOpen] = useState(false);
  const [fileName, setFileName] = useState<string>();
  const { t } = useTranslation("group");

  const { control, reset, handleSubmit, setError } = useForm<InviteToGroupForm>(
    {
      resolver: zodResolver(formSchema),
      defaultValues: {
        email: undefined,
        csv: undefined,
      },
    },
  );

  const utils = api.useUtils();

  const { mutateAsync } = api.person.invite.useMutation({
    onSuccess: () => utils.person.list.invalidate(),
  });

  const {
    isLoading,
    isSuccess,
    data,
    sendChunks,
    reset: resetChunkState,
  } = useSendChunks(mutateAsync);

  const messages = data?.flatMap((item) => item);

  const onSubmit = async (values: InviteToGroupForm) => {
    if (values.email) {
      await sendChunks([{ groupId: groupId, emails: [values.email] }]);
    }

    if (values.csv) {
      const data = await parseCsv<string[]>(values.csv, {
        delimiter: ",",
        delimitersToGuess: [",", "\t", "|", ";", " ", RECORD_SEP, UNIT_SEP],
        skipEmptyLines: true,
        transform: (value) => value.trim(),
      });

      const parsedEmails = data.data.flat().filter((email) => email !== "");

      const res = emailArraySchema.safeParse(parsedEmails);

      if (!res.success) {
        res.error.issues.map((error) => {
          setError("csv", {
            type: "manual",
            message: error.message,
          });
        });
      }

      if (res.success) {
        const emailsChunks = chunkArray(res.data, 50);
        const emailsWithGroupId = emailsChunks.map((chunk) => {
          return {
            groupId,
            emails: chunk,
          };
        });

        await sendChunks(emailsWithGroupId);
        reset();
        setFileName(undefined);
      }
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(state) => {
        setOpen(state);
        reset();
        setFileName(undefined);
      }}
    >
      <Dialog.Trigger data-id="invite-to-group-dialog-trigger" asChild>
        <Button variant="primary" size="md" rounded="lg">
          {t("invite-user.trigger")}
        </Button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay
          zIndex={0}
          key={"add-group-dialog-overlay"}
          style={{
            backgroundColor: "#6A696966",
            backdropFilter: "blur(10px)",
          }}
        />
        <GeneralDialog.Content
          data-id="invite-to-group-dialog-content"
          maxWidth={600}
          width="100%"
        >
          {isSuccess ? (
            <>
              <View
                width="100%"
                gap={"$5"}
                alignItems="center"
                paddingTop={"$5"}
                paddingBottom={"$4"}
              >
                <GeneralDialog.Title data-id="invite-to-group-dialog-success-title">
                  {t("invite-user.title.success")}
                </GeneralDialog.Title>
                {isLoading ? null : (
                  <ScrollView maxWidth={400} alignSelf="center" maxHeight={350}>
                    <View gap={"$3"} data-id="invite-to-group-dialog-messages">
                      {messages?.map((item, i) => (
                        <InviteStatusTable key={i} data={item} />
                      ))}
                    </View>
                  </ScrollView>
                )}
              </View>

              <GeneralDialog.ControllArea
                gap={"$2"}
                alignItems="center"
                paddingVertical={"$5.3"}
              >
                <Dialog.Close asChild>
                  <Button
                    data-id="invite-to-group-dialog-close-button"
                    variant="primary"
                    minWidth={180}
                    maxWidth={320}
                    width={"100%"}
                    size="md"
                    rounded="lg"
                    onPress={() => resetChunkState()}
                  >
                    {t("invite-user.close")}
                  </Button>
                </Dialog.Close>
              </GeneralDialog.ControllArea>
            </>
          ) : (
            <Form onSubmit={handleSubmit(onSubmit)}>
              <View
                width="100%"
                gap={"$5"}
                alignItems="center"
                paddingTop={"$5"}
                paddingBottom={"$4"}
              >
                <Controller
                  control={control}
                  name="email"
                  render={({ field, fieldState }) => (
                    <View gap={"$4"} width="100%" alignItems="center">
                      <GeneralDialog.Title data-id="invite-to-group-dialog-title">
                        {t("invite-user.title.default")}
                      </GeneralDialog.Title>
                      <InputWithLabel
                        data-id="invite-to-group-dialog-email-input"
                        maxWidth={400}
                        variant="inline"
                        size="md"
                        rounded="md"
                        label={t("invite-user.placeholder")}
                        placeholder={"mail@sample.com"}
                        isError={!!fieldState.error?.message}
                        error={fieldState.error?.message}
                        onChangeText={field.onChange}
                        {...field}
                      />
                    </View>
                  )}
                />

                <OrSeparator text={t("invite-user.or")} />

                <Controller
                  control={control}
                  name="csv"
                  render={({ field, fieldState }) => (
                    <View gap={"$2"} width="100%" alignItems="center">
                      <GeneralDialog.Title data-id="invite-to-group-dialog-multiple-title">
                        {t("invite-user.title.several")}
                      </GeneralDialog.Title>

                      <FileInput
                        data-id="invite-to-group-dialog-file-input"
                        maxWidth={400}
                        justifyContent="center"
                        mineTypes={FILE_MINE_TYPES}
                        alignItems="center"
                        //              label="Перетягніть сюди файл CSV"
                        error={fieldState.error?.message}
                        onChange={(e) => {
                          if (e.target?.files?.[0]) {
                            setFileName(e.target.files[0].name);
                            field.onChange(e.target.files[0]);
                          }
                        }}
                      >
                        {fileName ? (
                          <Text
                            data-id={"bag-report-dialog-file-name"}
                            fontFamily={"$body"}
                            fontWeight={"$4"}
                            fontSize={"$3"}
                            lineHeight={"$3"}
                            color={"$gray-800"}
                          >
                            {fileName}
                          </Text>
                        ) : (
                          <View>
                            <Text
                              data-id={"bag-report-dialog-file-placeholder"}
                              fontFamily={"$body"}
                              fontWeight={"$4"}
                              fontSize={"$3"}
                              lineHeight={"$3"}
                              color={"$gray-800"}
                            >
                              {t("invite-user.csv-instruction.first-line")}
                            </Text>
                            <Text
                              fontFamily={"$body"}
                              fontWeight={"$4"}
                              fontSize={"$3"}
                              lineHeight={"$3"}
                              color={"$lightblue-700"}
                              textDecorationLine="underline"
                            >
                              {t("invite-user.csv-instruction.second-line")}
                            </Text>
                          </View>
                        )}
                      </FileInput>
                    </View>
                  )}
                />
              </View>

              <GeneralDialog.ControllArea
                gap={"$2"}
                alignItems="center"
                paddingVertical={"$3"}
              >
                <Form.Trigger asChild>
                  <Button
                    data-id="invite-to-group-dialog-submit-button"
                    variant="primary"
                    minWidth={180}
                    maxWidth={320}
                    width={"100%"}
                    size="md"
                    rounded="lg"
                  >
                    {isLoading
                      ? t("invite-user.button.loading")
                      : t("invite-user.button.submit")}
                  </Button>
                </Form.Trigger>

                <GeneralDialog.Close
                  data-id="invite-to-group-dialog-cancel-button"
                  onPress={() => {
                    reset();
                    setFileName(undefined);
                  }}
                >
                  {t("invite-user.close")}
                </GeneralDialog.Close>
              </GeneralDialog.ControllArea>
            </Form>
          )}
        </GeneralDialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
};

//TODO: move to uikit
const OrSeparator: React.FC<{ text: string }> = ({ text }) => {
  return (
    <XStack width={"100%"} alignItems="center" maxWidth={400}>
      <Separator
        borderBottomWidth={"$0.2"}
        borderColor={"$gray-secondary-400"}
      />
      <Text
        paddingHorizontal={"$2"}
        fontFamily={"$subtitle"}
        fontWeight={"$4"}
        fontSize={"$2"}
        lineHeight={"$2"}
        color={"$gray-650"}
      >
        {text}
      </Text>
      <Separator
        borderBottomWidth={"$0.2"}
        borderColor={"$gray-secondary-400"}
      />
    </XStack>
  );
};
